/**
 * VerifyEmailModal - A modal component that prompts the user to enter their email
 * when it is not confirmed.
 *
 * This component is used to collect user information and used as a wrapper for inner components.
 *
 * Created by Ron on 2025/03/11.
 * Copyright (c) 2025-present iWoo Ltd. All rights reserved.
 */
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Modal from "react-modal";

import { useSignUpState } from "../../state/SignUpState";
import { useAppState } from "../../state";

// Components
import Button from "../Button/Button";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";

// Icons
import CloseIcon from "../../icons/CloseIcon";
import CheckNiceIcon from "../../icons/CheckNiceIcon";
import ArrowRightIcon from "../../icons/ArrowRightIcon";

// Utils
import { clientWidth } from "../../utils";
import { modalStyles } from "../../constants";

import Email from "./Email";

const TIME_TO_WAIT_BEFORE_SEND_AGAIN = 60;

const SendCodeAgainButton = ({ onClick }) => {
  const [sendCodeAgainTime, setSendCodeAgainTime] = useState(
    TIME_TO_WAIT_BEFORE_SEND_AGAIN
  );

  const intervalRef = useRef(null);
  const sendCodeAgainTimeRef = useRef(0);

  const startTimer = () => {
    sendCodeAgainTimeRef.current = TIME_TO_WAIT_BEFORE_SEND_AGAIN;

    intervalRef.current = setInterval(() => {
      sendCodeAgainTimeRef.current--;

      if (sendCodeAgainTimeRef.current <= 0) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;

        // Fix 0s on clicking the button
        setSendCodeAgainTime(TIME_TO_WAIT_BEFORE_SEND_AGAIN);

        return;
      }

      setSendCodeAgainTime(sendCodeAgainTimeRef.current);
    }, 1000);
  };

  useEffect(() => {
    // startTimer();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const handleClick = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    } else {
      startTimer();
    }

    onClick && onClick();
  }, [onClick]);

  return (
    <button
      className={`tbk-text-main tbk-mb-4 ${
        intervalRef.current !== null
          ? "tbk-cursor-disabled tbk-text-blue-grey"
          : "tbk-text-coral tbk-underline hover:tbk-text-coral-light"
      }`}
      disabled={intervalRef.current !== null}
      onClick={handleClick}
    >
      {intervalRef.current !== null ? (
        `Send again ... in ${sendCodeAgainTime}s`
      ) : (
        <span>
          Send Confirmation Email Again
          <ArrowRightIcon className="tbk-inline" />
        </span>
      )}
    </button>
  );
};

const VerifyEmailModal = ({
  children,
  email,
  busy,
  isOpen,
  onSubmit,
  onClose,
}) => {
  const location = useLocation();
  const history = useHistory();
  const { theme } = useTheme();

  const {
    user,
    hasTriedToAutoLogin,
    isLoggedIn,
    sendEmailVerificationRequest,
    isFetching,
  } = useAppState();

  const [_isOpen, setIsOpen] = useState(isOpen);
  const [hasShownVerificationPopup, setHasShownVerificationPopup] =
    useState(false);

  const customStyle =
    clientWidth() < theme.responsive.breakpoints.sm
      ? Object.assign(
          {},
          { ...modalStyles.mobileDefault },
          {
            overlay: {
              ...modalStyles.mobileDefault.overlay,
              backgroundColor: "white",
              zIndex: 1060,
            },
            content: {
              ...modalStyles.mobileDefault.content,
              top: 0,
              padding: "24px",
            },
          }
        )
      : Object.assign(
          {},
          { ...modalStyles.default },
          {
            overlay: {
              ...modalStyles.default.overlay,
              backgroundColor: "white",
              zIndex: 1060,
            },
            content: {
              ...modalStyles.default.content,
              width: "428px",
              zIndex: 1060,
            },
          }
        );

  const handleOpenModal = () => {
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setIsOpen(false);

    onClose && onClose();
  };

  useEffect(() => {
    const hasShownPopup = sessionStorage.getItem("hasShownVerificationPopup");
    if (hasShownPopup === "true") {
      setHasShownVerificationPopup(true);
    }
  }, []);

  useEffect(() => {
    // Went from Success or Cancel pages
    const purchaseComplete = sessionStorage.getItem("purchaseComplete");
    let hasShownPopup;

    if (!hasShownVerificationPopup) {
      hasShownPopup = sessionStorage.getItem("hasShownVerificationPopup");
    }

    if (
      !isLoggedIn ||
      purchaseComplete !== null ||
      !user ||
      // location.pathname === "/" ||
      location.pathname === "/become-a-member" ||
      location.pathname === "/login" ||
      location.pathname === "/checkout" ||
      location.pathname === "/questionnaire"
    ) {
      if (isLoggedIn) {
        sessionStorage.removeItem("purchaseComplete");
      }

      return;
    }

    if (!hasShownVerificationPopup && !user.emailConfirmed && !hasShownPopup) {
      handleOpenModal();
      setHasShownVerificationPopup(true);
      sessionStorage.setItem("hasShownVerificationPopup", "true");
      // handleSubmit();
    }
  }, [location, isLoggedIn, user, hasShownVerificationPopup]);

  useEffect(() => {
    // When logged out
    if (!hasTriedToAutoLogin) {
      return;
    }
    if (!user && hasShownVerificationPopup) {
      setHasShownVerificationPopup(false);
      sessionStorage.removeItem("hasShownVerificationPopup");
    }
  }, [user, hasShownVerificationPopup, hasTriedToAutoLogin]);

  const handleSubmit = (val) => {
    if (user) {
      sendEmailVerificationRequest(user.id).then(() => {
        // handleCloseModal();
      });
    }
  };

  return (
    <Modal
      appElement={/*__CLIENT__ && */ document.getElementById("#app")}
      isOpen={_isOpen}
      // onAfterOpen={this.afterOpenModal}
      onRequestClose={() => handleCloseModal()}
      style={customStyle}
      contentLabel="Email Verification Modal"
    >
      <div className="tbk-text-center">
        <div className="tbk-text-title-bold tbk-text-primay tbk-mb-4">
          Let's Verify Your Email
        </div>
        <Email className="tbk-mx-auto tbk-mb-4" />
        <p className="tbk-text-main tbk-text-primay tbk-mb-4">
          We’ve sent a quick verification link to
          <br />
          {email || user?.email}. Simply open your email and click the link to
          confirm your address.
        </p>
        <p className="tbk-text-main tbk-text-primay tbk-mb-4 !tbk-font-medium">
          <span className="tbk-text-coral">Note</span>: Verifying your email
          ensures you receive all essential updates, including class bookings,
          receipts, and exclusive TruBe perks.
        </p>

        <SendCodeAgainButton onClick={handleSubmit} />

        <Button
          className="tbk-mb-3 tbk-w-full"
          //variant="secondary"
          disabled={busy || isFetching}
          endIcon={<CheckNiceIcon />}
          showLoader={busy || isFetching}
          onClick={(e) => {
            e.preventDefault();
            handleCloseModal();
          }}
        >
          Ok
        </Button>
      </div>
    </Modal>
  );
};

VerifyEmailModal.displayName = "VerifyEmailModal";

export default VerifyEmailModal;
